import { useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import removeSvg from 'assets/icons/remove.svg';
import warningSvg from 'assets/icons/warning.svg';
import copyIcon from 'assets/icons/copy.svg';
import editSvg from 'assets/icons/edit.svg';
import { Store } from 'components/common/types/Store.types';
import { IconSize, InputShape, InputSize, InputTheme } from 'components/common/types/Input.types';
import Modal from 'lib/Modal';
import Input from 'lib/Input';
import Button, { Color, Size } from 'lib/Button';
import IconButton from 'lib/IconButton';
import Tooltip, { TooltipDirection, TooltipSize } from 'lib/Tooltip';
import {
  duplicateProductLine,
  syncActiveLineFromNewProduct,
  removeProductLine,
  changeProductLineName,
  syncNewProductFromActiveLine,
} from 'store/productManagement/reducer';
import { getActiveProductLine, getIsReadOnly } from 'store/productManagement/selectors';
import { hasProductRequiredFields } from 'utils/hasProductRequiredFields';
import { isEmpty } from 'lodash';
import { SelectionPermission } from 'store/productManagement/reducer.types';

const LINE_NAME_MAX_LENGTH = 60;

const getDeleteTooltip = (isReadOnly: boolean, isSingleLine: boolean): string => {
  if (isReadOnly) {
    return 'Cannot edit in read-only mode';
  }
  if (isSingleLine) {
    return 'Cannot delete the only line';
  }
  return 'Delete line';
};

const getDuplicateTooltip = (isReadOnly: boolean, hasRequiredFields: boolean): string => {
  if (isReadOnly) {
    return 'Cannot duplicate in read-only mode';
  }
  if (!hasRequiredFields) {
    return 'Cannot duplicate as required fields are not filled';
  }
  return 'Duplicate line';
};

const ProductLineActions: React.FC = () => {
  const dispatch = useDispatch();
  const activeLine = useSelector(getActiveProductLine);
  const productLines = useSelector((state: Store) => state.productManagement.productLines);
  const isReadOnly = useSelector(getIsReadOnly);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [lineName, setLineName] = useState('');

  const { mediaType, channels, pricingMode, range, budget, impressions, frames, networks } = useSelector(
    (state: Store) => state.productManagement.newProduct,
    shallowEqual,
  );

  const areNetworksEmpty = isEmpty(networks) || networks?.selectionPermission === SelectionPermission.DISABLED;
  const hasRequiredFields = hasProductRequiredFields({
    mediaType,
    channels,
    pricingMode,
    range,
    budget,
    impressions,
    frames,
    areNetworksEmpty,
  });
  const isDuplicateDisabled = isReadOnly || !hasRequiredFields;
  const isSingleLine = productLines.length <= 1;
  const isDeleteDisabled = isReadOnly || isSingleLine;

  const handleLineNameBlur = (): void => {
    const trimmedName = lineName.trim();
    if (trimmedName.length > 0) {
      dispatch(changeProductLineName({ lineId: activeLine.lineId, name: trimmedName }));
    }
  };

  const handleDeleteLine = (): void => {
    if (activeLine) {
      dispatch(removeProductLine(activeLine.lineId));
      dispatch(syncNewProductFromActiveLine());
    }
    setIsDeleteModalOpen(false);
  };

  const handleDuplicateLine = (): void => {
    dispatch(syncActiveLineFromNewProduct());
    dispatch(duplicateProductLine());
    dispatch(syncNewProductFromActiveLine());
  };

  return (
    <div
      data-test-id="product-line-actions"
      className="flex justify-between items-center py-2 border-b border-neutral-950-opacity-10"
    >
      <div data-test-id="product-line-name" className="-ml-2">
        <Input
          onBlur={handleLineNameBlur}
          value={activeLine.name}
          dataTestId="product-line-name-input"
          theme={InputTheme.ITALIC_WHITE}
          placeholder="Line name"
          onChange={(value: string) => setLineName(value)}
          endIconSvg={!isReadOnly ? editSvg : undefined}
          iconSize={IconSize.EXTRA_SMALL}
          size={InputSize.EXTRA_SMALL}
          shape={InputShape.ROUNDED_MEDIUM}
          className="py-1"
          name="Line name"
          isDisabled={isReadOnly}
          maxLength={LINE_NAME_MAX_LENGTH}
        />
      </div>
      <div className="flex items-center space-x-1">
        <Tooltip
          tooltip={getDuplicateTooltip(isReadOnly, hasRequiredFields)}
          direction={TooltipDirection.BOTTOM}
          contentSize={TooltipSize.MAX}
        >
          <IconButton
            dataTestId="duplicate-product-line-button"
            icon={copyIcon}
            name="Duplicate line"
            handleOnClick={handleDuplicateLine}
            isDisabled={isDuplicateDisabled}
          />
        </Tooltip>
        <Tooltip
          tooltip={getDeleteTooltip(isReadOnly, isSingleLine)}
          direction={TooltipDirection.BOTTOM}
          contentSize={TooltipSize.MAX}
        >
          <IconButton
            dataTestId="delete-product-line-button"
            icon={removeSvg}
            name="Delete line"
            isDisabled={isDeleteDisabled}
            handleOnClick={() => setIsDeleteModalOpen(true)}
          />
        </Tooltip>
      </div>

      <Modal
        dataTestId="delete-product-line-modal"
        title="Delete line"
        text="Are you sure you want to clear the current deal line? All of your data will be permanently removed. This action cannot be undone."
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        icon={warningSvg}
        actionButtons={
          <>
            <Button
              dataTestId="delete-product-line-modal-cancel"
              label="Cancel"
              size={Size.MEDIUM}
              color={Color.SECONDARY}
              onClick={() => setIsDeleteModalOpen(false)}
            />
            <Button
              dataTestId="delete-product-line-modal-confirm"
              label="Delete line"
              size={Size.MEDIUM}
              color={Color.DANGER}
              onClick={handleDeleteLine}
            />
          </>
        }
      />
    </div>
  );
};

export default ProductLineActions;
